/**
 * Nested accordion component uses dynamic data for creating nested children as tabbed interface
 *
 * @module views/components/NestedAccordionListItem
 * @memberof -Common
 */
import './NestedAccordionListItem.scss';

import React, { useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { triggerDatacaptureEvent } from '@ulta/core/utils/datacapture/datacapture';

import { useFiltersColumnContext } from '@ulta/providers/FiltersColumnProvider/FiltersColumnProvider';

import { FILTER_GROUPS } from '@ulta/utils/constants/filters';
/**
 * Represents a NestedAccordionListItem component
 *
 * @method
 * @param {NestedAccordionListItemProps} props - React properties passed from composition
 * @returns NestedAccordionListItem
 */
export const NestedAccordionListItem = function( props ){
  const {
    category,
    componentKey,
    level,
    seeMoreLabel,
    viewLessLabel,
    seeMoreListLength,
    categoryData,
    isFirstItem,
    isLastItem,
    hideItems
  } = props;
  const [seeMoreToggle, setSeeMoreToggle] = useState( false );
  const { seeMore, setSeeMore, toggleFilterGroupListItem } =
    useFiltersColumnContext();
  if( !category || !category.title ){
    return null;
  }

  const subCategory = categoryData?.filter( ( item ) => item.level === level + 1 );

  const subCategoryList =
    seeMore[`${category.title}-${level}`] !== false ?
      subCategory :
      subCategory;

  const categoryLevel = category.level || 1;
  return (
    <li className={ classNames( 'NestedAccordionListItem--has_children',
      {
        'NestedAccordionListItem__hide': hideItems
      }
    ) }
    >
      <Link_Huge
        url={ category?.applyFilterSEOUrl }
        className={ classNames(
          `NestedAccordionListItem__Label NestedAccordionListItem__Label--Level${categoryLevel}`,
          {
            'NestedAccordionListItem__Label--isFirstItem': isFirstItem,
            'NestedAccordionListItem__Label--isLastItem': isLastItem,
            'NestedAccordionListItem__disabledAnchor': category.disabled
          }
        ) }
        onClick={ onFilterClick(
          {
            setSeeMore,
            setSelectedFilter: toggleFilterGroupListItem
          },
          {
            filterItem: { ...category }
          }
        ) }
        unstyled={ true }
      >
        <div
          className={ classNames( 'NestedAccordionListItem__container', {
            'NestedAccordionListItem--disabled': category.disabled
          } ) }
        >
          <Text
            htmlTag='p'
            textStyle='body-1'
            fontWeight={
              ( subCategory && subCategory.length > 0 ) || category.selected ?
                'bold' :
                ''
            }
          >
            <>
              { category.title }
              { category.count > 0 &&
                <span className='NestedAccordionListItem__count'>
                  <span
                    className='sr-only'
                    data-nosnippet='true'
                  >
                    { category.availableCountLabel }
                  </span>
                  <Text
                    htmlTag='span'
                    color='neutral-600'
                    textStyle='body-3'
                    ariaHidden={ true }
                    noSnippet={ true }
                  >
                    { category.count }
                  </Text>
                </span>
              }
            </>
          </Text>
        </div>
      </Link_Huge>
      { subCategory && subCategory.length > 0 && (
        <ul className={ `NestedAccordionListItem__List NestedAccordionListItem__List--Level${level}` }>
          { subCategoryList.map( ( subcategory, i ) => (
            <NestedAccordionListItem
              key={ `${i}-subcat-${componentKey}-${subcategory.id}` }
              category={ subcategory }
              level={ level + 1 }
              seeMoreLabel={ seeMoreLabel }
              viewLessLabel={ viewLessLabel }
              seeMoreListLength={ seeMoreListLength }
              categoryData={ categoryData }
              isFirstItem={ i === 0 }
              isLastItem={ ( i === seeMoreListLength - 1 && !seeMoreToggle ) || ( seeMoreToggle && i === subCategory.length - 1 ) }
              hideItems={ i > seeMoreListLength - 1 && !seeMoreToggle }
            />
          ) ) }
          { seeMore[`${category.title}-${level}`] !== false &&
            subCategory.length > seeMoreListLength && (
            <li
              className={ `NestedAccordionListItem__SeeMore--Level${level + 1
                  }` }
            >
              <Button
                likeLink
                label={ seeMoreLabel }
                onClick={ () => {
                  setSeeMoreToggle( true );
                  setSeeMore( {
                    ...( Object.keys( seeMore ).length && seeMore ),
                    [`${category.title}-${level}`]: false
                  } );
                }
                }
              />
            </li>
          ) }
          { seeMore[`${category.title}-${level}`] === false && (
            <li
              className={ `NestedAccordionListItem__ViewLess--Level${level + 1}` }
            >
              <Button
                likeLink
                label={ viewLessLabel }
                onClick={ () => {
                  setSeeMoreToggle( false );
                  setSeeMore( {
                    ...( Object.keys( seeMore ).length && seeMore ),
                    [`${category.title}-${level}`]: true
                  } );
                }
                }
              />
            </li>
          ) }
        </ul>
      ) }
    </li>
  );
};

/**
 * @method onFilterClick
 * @summary Nested accordion filter item click handler
 * @param {object} methods - passing setSeeMore, setSelectedFilter functions
 * @param {object} data - passing filterItem object
 * @returns {undefined}
 */
export const onFilterClick = ( methods, data ) => ( e ) => {
  e.preventDefault();
  const { setSeeMore, setSelectedFilter } = methods;
  const { filterItem } = data;
  setSeeMore( {} );

  if( setSelectedFilter ){
    setSelectedFilter( filterItem );
  }

  const dataCaptureData = data.filterItem?.selectAction?.dataCaptureData;
  if(
    data.filterItem.groupType === FILTER_GROUPS.CATEGORIES &&
    dataCaptureData
  ){
    triggerDatacaptureEvent( dataCaptureData );
  }
};

/**
 * Property type definitions
 *
 * @typedef NestedAccordionListItemProps
 * @type {object}
 * @property {array} categoryData - Object containing nested accordion list items (name, subcategories)
 * @property {number} level - current filter level in hierarchy
 * @property {string} seeMoreLabel - Sets the See More with item count Label
 * @property {string} viewLessLabel - Sets the view less Label
 * @property {number} seeMoreListLength - List of items to be displayed before seeMore button
 */
export const propTypes = {
  category: PropTypes.shape( {
    title: PropTypes.string
  } ),
  categoryData: PropTypes.array,
  level: PropTypes.number,
  seeMoreLabel: PropTypes.string,
  viewLessLabel: PropTypes.string,
  seeMoreListLength: PropTypes.number
};

NestedAccordionListItem.propTypes = propTypes;

export default NestedAccordionListItem;
